import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaCheck, FaTimes, FaPlus, FaSave } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import AIBroadcastStickyHeader from './AIBroadcastHeader';

const IA_TABS = [
  { key: 'from', label: 'Expéditeur' },
  { key: 'subject', label: 'Objet' },
  { key: 'html', label: 'HTML' },
  { key: 'text', label: 'Texte' },
];

export default function AIBroadcastModerationList() {
  const { broadcastId } = useParams();
  const [master, setMaster] = useState({
    from_name: '',
    subject: '',
    html_content: '',
    text_content: '',
  });
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [tab, setTab] = useState('from');
  const [variants, setVariants] = useState({
    from: [],
    subject: [],
    html: [],
    text: [],
  });
  const [fetchingIA, setFetchingIA] = useState(false);
  const [error, setError] = useState('');
  const [editBuffer, setEditBuffer] = useState({
    from_name: '',
    subject: '',
    html_content: '',
    text_content: '',
  });

  // Modération/édition
  const [selectedIdx, setSelectedIdx] = useState(-1); // -1 = master
  const [status, setStatus] = useState({}); // { "tab-idx": "validated"|"rejected" }

  // Hydrate master avec le 1er message associé du broadcast
  useEffect(() => {
    async function fetchMasterMsg() {
      const token = localStorage.getItem('prae.jwt');
      setLoadingMaster(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/messages`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!res.ok) throw new Error('Erreur messages');
        const msgs = await res.json();
        const first = Array.isArray(msgs) && msgs.length ? msgs[0] : {};
        setMaster({
          from_name: first.from_name || '',
          subject: first.subject || '',
          html_content: first.html_content || '',
          text_content: '', // master texte IA = vide
        });
        setEditBuffer({
          from_name: first.from_name || '',
          subject: first.subject || '',
          html_content: first.html_content || '',
          text_content: '',
        });
        setSelectedIdx(-1); // Reset sur master
      } catch (e) {
        setMaster({
          from_name: '',
          subject: '',
          html_content: '',
          text_content: '',
        });
      }
      setLoadingMaster(false);
    }
    if (broadcastId) fetchMasterMsg();
  }, [broadcastId]);

  // Récupère la valeur à éditer selon le tab et la sélection
  function getField(field) {
    if (selectedIdx === -1) {
      if (tab === 'from') return master.from_name || '';
      if (tab === 'subject') return master.subject || '';
      if (tab === 'html') return master.html_content || '';
      if (tab === 'text') return ''; // pas de master text
    } else {
      if (tab === 'from') return variants[tab][selectedIdx]?.from_name || '';
      if (tab === 'subject') return variants[tab][selectedIdx]?.subject || '';
      if (tab === 'html') return variants[tab][selectedIdx]?.html_content || '';
      if (tab === 'text') return variants[tab][selectedIdx]?.text_content || '';
    }
    return '';
  }

  // Synchronise l'édition sur changement
  useEffect(() => {
    setEditBuffer({
      from_name: getField('from'),
      subject: getField('subject'),
      html_content: getField('html'),
      text_content: getField('text'),
    });
    // eslint-disable-next-line
  }, [tab, selectedIdx, variants, master]);

  // Génération IA (call API back)
  async function handleGenerateIA() {
    const token = localStorage.getItem('prae.jwt');
    setFetchingIA(true);
    setError('');
    try {
      let endpoint = '';
      if (tab === 'from') endpoint = '/generate-from';
      if (tab === 'subject') endpoint = '/generate-subject';
      if (tab === 'html') endpoint = '/generate-html-variant';
      if (tab === 'text') endpoint = '/generate-text';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ master }),
        }
      );
      const data = await response.json();
      if (!data.variants) throw new Error('Aucune variante IA reçue.');
      setVariants(v => ({ ...v, [tab]: data.variants }));
      setSelectedIdx(0);
      setStatus(s => ({ ...s })); // reset status pour cette tab
    } catch (e) {
      setError(e.message || 'Erreur API');
    }
    setFetchingIA(false);
  }

  // Validation/refus (depuis la colonne droite ou la card)
  const handleValidate = idx =>
    setStatus(s => ({ ...s, [`${tab}-${idx}`]: 'validated' }));
  const handleReject = idx =>
    setStatus(s => ({ ...s, [`${tab}-${idx}`]: 'rejected' }));

  // Edition live
  const handleEditChange = (field, value) => {
    setEditBuffer(prev => ({ ...prev, [field]: value }));
  };
  const handleSave = () => {
    if (selectedIdx === -1) {
      setMaster(editBuffer);
    } else {
      setVariants(vars => ({
        ...vars,
        [tab]: vars[tab].map((v, i) =>
          i === selectedIdx
            ? {
                ...v,
                from_name: editBuffer.from_name,
                subject: editBuffer.subject,
                html_content: editBuffer.html_content,
                text_content: editBuffer.text_content,
              }
            : v
        ),
      }));
    }
  };

  // Bloc modération au dessus du form (toujours affichée)
  function ModerationBar() {
    // Pas de modération sur master
    if (selectedIdx === -1) return null;
    const st = status[`${tab}-${selectedIdx}`];
    return (
      <div className="flex gap-3 items-center mb-3">
        <button
          className={`p-2 rounded-full border-2 ${st === 'validated' ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'} hover:bg-green-100`}
          onClick={() => handleValidate(selectedIdx)}
          title="Valider"
        >
          <FaCheck
            className={st === 'validated' ? 'text-green-600' : 'text-gray-500'}
          />
        </button>
        <button
          className={`p-2 rounded-full border-2 ${st === 'rejected' ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'} hover:bg-red-100`}
          onClick={() => handleReject(selectedIdx)}
          title="Refuser"
        >
          <FaTimes
            className={st === 'rejected' ? 'text-red-600' : 'text-gray-500'}
          />
        </button>
        {st && (
          <span
            className={`ml-2 text-xs font-bold ${st === 'validated' ? 'text-green-700' : 'text-[#a1566d]'}`}
          >
            {st === 'validated' ? 'Validé' : 'Refusé'}
          </span>
        )}
      </div>
    );
  }

  // Card affichage
  function Card({ variant, idx, active, onClick, isMaster, tab }) {
    let label = '';
    if (tab === 'from') label = variant.from_name || '';
    if (tab === 'subject') label = variant.subject || '';
    if (tab === 'html') {
      let plain = '';
      if (variant.html_content) {
        // Extraction texte sans balise
        const tmp = document.createElement('div');
        tmp.innerHTML = variant.html_content;
        plain = tmp.innerText || tmp.textContent || '';
        if (plain.length > 80) plain = plain.slice(0, 80) + '…';
      }
      label = plain;
    }
    if (tab === 'text')
      label = variant.text_content
        ? variant.text_content.slice(0, 80) + '…'
        : '';

    const st = status[`${tab}-${idx}`];
    return (
      <div
        className={`mb-3 rounded-xl px-4 py-2 flex items-center gap-2 bg-white border-2 ${
          active ? 'border-[#a56dff] ring-2' : 'border-[#eceaff]'
        } cursor-pointer group`}
        style={{ minHeight: 56 }}
        onClick={onClick}
        tabIndex={0}
        aria-selected={active}
        role="option"
      >
        <div className="flex-1 font-medium text-sm text-[#23223a] truncate">
          {isMaster && (
            <span className="font-bold text-[#a56dff] mr-2">MASTER</span>
          )}
          {label}
        </div>
        {!isMaster && (
          <>
            <button
              className={`p-2 rounded-full hover:bg-green-50 border ${st === 'validated' ? 'border-green-500' : 'border-gray-300'}`}
              onClick={e => {
                e.stopPropagation();
                handleValidate(idx);
              }}
              tabIndex={-1}
            >
              <FaCheck
                className={
                  st === 'validated' ? 'text-green-700' : 'text-gray-500'
                }
              />
            </button>
            <button
              className={`p-2 rounded-full hover:bg-red-50 border ${st === 'rejected' ? 'border-red-400' : 'border-gray-300'}`}
              onClick={e => {
                e.stopPropagation();
                handleReject(idx);
              }}
              tabIndex={-1}
            >
              <FaTimes
                className={st === 'rejected' ? 'text-red-600' : 'text-gray-500'}
              />
            </button>
          </>
        )}
      </div>
    );
  }

  // Bloc d’édition/preview
  const selectedVariant =
    selectedIdx === -1 ? master : variants[tab][selectedIdx] || {};

  return (
    <div className="min-h-screen w-full bg-[#F4F4F6]">
      <AIBroadcastStickyHeader tab={tab} setTab={setTab} />
      <div className="flex mt-16 flex-col md:flex-row pt-0 min-h-[calc(100vh-64px)]">
        {/* Colonne gauche */}
        <div className="w-full md:w-1/4 px-6 py-10 border-r border-[#e2e2e7] bg-[#F4F4F6]">
          <div className="flex items-center mb-6">
            <button
              className="ml-auto px-4 py-2 bg-gradient-to-tr from-[#a56dff] to-[#ccf3df] text-[#23223a] font-bold rounded-xl hover:scale-105 transition flex items-center gap-2"
              onClick={handleGenerateIA}
              disabled={fetchingIA}
            >
              <FaPlus /> Générer IA
            </button>
          </div>
          {/* Master Card */}
          <Card
            variant={master}
            idx={-1}
            active={selectedIdx === -1}
            onClick={() => setSelectedIdx(-1)}
            isMaster={true}
            tab={tab}
          />
          {error && <div className="text-red-400 font-bold">{error}</div>}
          {/* Variants */}
          <AnimatePresence>
            {variants[tab].map((variant, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.18, delay: idx * 0.03 }}
              >
                <Card
                  variant={variant}
                  idx={idx}
                  active={selectedIdx === idx}
                  onClick={() => setSelectedIdx(idx)}
                  isMaster={false}
                  tab={tab}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        {/* Colonne droite : modération + édition toujours visible */}
        <div className="w-full md:w-3/4 bg-white md:px-10 px-2 py-10 relative min-h-[350px]">
          {fetchingIA ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full h-full flex flex-col justify-center items-center"
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                className="mb-5"
              >
                <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                  <circle
                    cx="28"
                    cy="28"
                    r="24"
                    stroke="#a56dff"
                    strokeWidth="8"
                    strokeDasharray="64 100"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[#a56dff] text-lg font-semibold"
              >
                Génération IA en cours…
              </motion.div>
            </motion.div>
          ) : (
            <div>
              <ModerationBar />
              <motion.form
                key={'editform-' + tab + '-' + selectedIdx}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.18 }}
                className="bg-[#f6f6fa] p-8 rounded-2xl border-2 border-[#e5edfa] shadow-md mt-1"
                onSubmit={e => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                {tab === 'from' && (
                  <div>
                    <label className="font-bold mb-2 block">
                      Nom d’expéditeur
                    </label>
                    <input
                      className="w-full rounded border px-3 py-2 mb-4"
                      value={editBuffer.from_name}
                      onChange={e =>
                        handleEditChange('from_name', e.target.value)
                      }
                    />
                  </div>
                )}
                {tab === 'subject' && (
                  <div>
                    <label className="font-bold mb-2 block">Objet</label>
                    <input
                      className="w-full rounded border px-3 py-2 mb-4"
                      value={editBuffer.subject}
                      onChange={e =>
                        handleEditChange('subject', e.target.value)
                      }
                    />
                  </div>
                )}
                {tab === 'html' && (
                  <div>
                    <label className="font-bold mb-2 block">HTML</label>
                    <textarea
                      className="w-full rounded border px-3 py-2 mb-4"
                      rows={6}
                      value={editBuffer.html_content}
                      onChange={e =>
                        handleEditChange('html_content', e.target.value)
                      }
                    />
                    <div className="font-bold mb-1 mt-2">Preview live</div>
                    <div
                      className="border rounded bg-[#fafbfc] p-2 text-xs"
                      dangerouslySetInnerHTML={{
                        __html: editBuffer.html_content,
                      }}
                    />
                  </div>
                )}
                {tab === 'text' && (
                  <div>
                    <label className="font-bold mb-2 block">Texte brut</label>
                    <textarea
                      className="w-full rounded border px-3 py-2 mb-4"
                      rows={4}
                      value={editBuffer.text_content}
                      onChange={e =>
                        handleEditChange('text_content', e.target.value)
                      }
                    />
                  </div>
                )}
                <div className="flex gap-4 mt-6">
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl bg-[#a56dff] text-white font-bold hover:bg-[#9145c7] flex items-center gap-2"
                  >
                    <FaSave /> Enregistrer
                  </button>
                </div>
              </motion.form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
